import { AssociationMember } from "@boark/shared/types/association-member";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { electedRepresentativeTypeOptions } from "../../components/user-form";
import { useGetAssociationMembers } from "../../queries/association-member";
import { useGetAuthContext } from "../../queries/get-auth-context";
import { useLocale } from "../../stores/locale";
import { recordIdToId } from "../../utils/record-id-to-id";
import { UserCreate } from "./user-create";
import { UserEdit } from "./user-edit";

export function UserList() {
  const { locale: t, get } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: members, isLoading: isMembersLoading } =
    useGetAssociationMembers();
  const { data: authContext } = useGetAuthContext();
  const isOwner =
    authContext?.type === "associationUser" && authContext.isOwner;
  const associationName =
    authContext?.type === "associationUser"
      ? authContext.association.name
      : undefined;

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Routes>
        <Route element={<UserCreate />} path="/create" />
        <Route element={<UserEdit />} path="/:id/edit" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col gap-1">
            <H1>{t.user.users}</H1>
            {associationName && (
              <p className="text-sm text-gray-600">
                {t.user.forAssociation}{" "}
                <span className="font-medium text-gray-800">
                  {associationName}
                </span>
              </p>
            )}
          </div>
          {isOwner && (
            <Button route="/admin/user/list/create">{t.user.create}</Button>
          )}
        </div>
        <Table
          data={members || []}
          loading={isMembersLoading}
          editRouteButton={
            isOwner
              ? (row) => `/admin/user/list/${recordIdToId(row.id)}/edit`
              : undefined
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          sortBy={searchParams.get("sortBy") || "email"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({ filterBy: JSON.stringify(value) });
          }}
          onSort={(field, direction) => {
            updateSearchParams({ sortBy: field, sortByDirection: direction });
          }}
        >
          <Column field="email">{t.user.email}</Column>
          <Column field="firstName">{get("user.firstName")}</Column>
          <Column field="lastName">{get("user.lastName")}</Column>
          <Column
            body={(row: AssociationMember) =>
              row.isOwner ? t.auth.memberTypeOwner : t.auth.memberTypeMember
            }
            field="isOwner"
            filterableBy="select"
            filterOptions={() => [
              { label: t.auth.memberTypeOwner, value: true },
              { label: t.auth.memberTypeMember, value: false },
            ]}
          >
            {t.user.type}
          </Column>
          <Column
            body={(row: AssociationMember) =>
              electedRepresentativeTypeOptions(t).find(
                (option) => option.value === row.electedRepresentativeType,
              )?.label ?? ""
            }
            field="electedRepresentativeType"
            filterableBy="select"
            filterOptions={() => electedRepresentativeTypeOptions(t)}
          >
            {t.user.electedRepresentativeType.label}
          </Column>
          <Column field="active">{t.user.active}</Column>
        </Table>
      </div>
    </>
  );
}
